// Shared UI kit for Finance Portal agent pages.
// Primitives: MetricCard, TrendChart, DonutChart, BarChart, SummaryGrid,
// SectionHeader, StatusPill, EmptyState, LoadingBlock, ConfidenceBar,
// DrillTable, AIAnalysisBox, ActionBar, NumberStat, MiniSpark.
// All themed against financeTheme (QB-inspired palette).
import React from 'react';
import {
    Box, Paper, Typography, Stack, Chip, CircularProgress, Button,
    LinearProgress, Skeleton, Table, TableHead, TableBody, TableRow,
    TableCell, Tooltip,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import InboxIcon from '@mui/icons-material/Inbox';
import {
    LineChart, Line, BarChart, Bar, AreaChart, Area,
    PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
    Tooltip as RTooltip, ResponsiveContainer, Legend,
    RadialBarChart, RadialBar,
} from 'recharts';

// ---------- Palette ----------
export const CHART_COLORS = {
    primary: '#2CA01C',
    secondary: '#0077C5',
    warning: '#F2A922',
    danger: '#D52B1E',
    success: '#10B981',
    info: '#0EA5E9',
    neutral: '#6B7280',
    muted: '#E5E7EB',
    palette: ['#2CA01C', '#0077C5', '#F2A922', '#D52B1E', '#7C3AED', '#EC4899', '#14B8A6', '#F97316'],
};

// ---------- Formatters ----------
export const fmtMoney = (n, currency = 'USD') => {
    if (n == null || isNaN(n)) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);
};
export const fmtMoneyFull = (n, currency = 'USD') => {
    if (n == null || isNaN(n)) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n);
};
export const fmtNumber = (n) => (n == null || isNaN(n)) ? '-' : new Intl.NumberFormat('en-US').format(n);
export const fmtPct = (n, digits = 1) => (n == null || isNaN(n)) ? '-' : `${(n * 100).toFixed(digits)}%`;
export const fmtDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch (_) { return d; }
};

// ---------- MetricCard ----------
// label + big value + optional delta + optional sparkline
export function MetricCard({
    label, value, sublabel,
    delta, deltaDirection, deltaTooltip,
    spark, sparkColor = CHART_COLORS.primary,
    accent = 'primary', icon, onClick,
}) {
    const accentColor = {
        primary: CHART_COLORS.primary,
        secondary: CHART_COLORS.secondary,
        warning: CHART_COLORS.warning,
        danger: CHART_COLORS.danger,
        success: CHART_COLORS.success,
        info: CHART_COLORS.info,
        neutral: CHART_COLORS.neutral,
    }[accent] || CHART_COLORS.primary;

    const DirIcon = deltaDirection === 'up' ? TrendingUpIcon
        : deltaDirection === 'down' ? TrendingDownIcon : TrendingFlatIcon;
    const deltaColor = deltaDirection === 'up' ? CHART_COLORS.success
        : deltaDirection === 'down' ? CHART_COLORS.danger : CHART_COLORS.neutral;

    return (
        <Paper
            variant="outlined"
            onClick={onClick}
            sx={{
                p: 1.5, minHeight: 84,
                borderLeft: `3px solid ${accentColor}`,
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all .15s ease',
                '&:hover': onClick ? { boxShadow: 1, transform: 'translateY(-1px)' } : {},
            }}
        >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {icon}
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {label}
                        </Typography>
                    </Stack>
                    <Typography variant="h5" sx={{ fontWeight: 700, fontSize: 22, lineHeight: 1.2, color: 'text.primary' }}>
                        {value}
                    </Typography>
                    {sublabel && (
                        <Typography variant="caption" color="text.secondary">{sublabel}</Typography>
                    )}
                    {delta != null && (
                        <Tooltip title={deltaTooltip || ''} arrow disableHoverListener={!deltaTooltip}>
                            <Stack direction="row" spacing={0.25} alignItems="center">
                                <DirIcon sx={{ fontSize: 14, color: deltaColor }} />
                                <Typography variant="caption" sx={{ color: deltaColor, fontWeight: 600 }}>
                                    {delta}
                                </Typography>
                            </Stack>
                        </Tooltip>
                    )}
                </Stack>
                {spark && spark.length > 1 && (
                    <Box sx={{ width: 80, height: 36, flexShrink: 0 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={spark.map((v, i) => ({ i, v }))}>
                                <Line type="monotone" dataKey="v" stroke={sparkColor} strokeWidth={1.5} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                )}
            </Stack>
        </Paper>
    );
}

// ---------- SummaryGrid ----------
export function SummaryGrid({ cards, cols }) {
    const c = cols || Math.min(cards.length, 4);
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: `repeat(${c}, 1fr)` }, gap: 1 }}>
            {cards.map((card, i) => <MetricCard key={i} {...card} />)}
        </Box>
    );
}

// ---------- TrendChart (line/area) ----------
export function TrendChart({
    data, xKey, series, height = 260, type = 'line',
    yFormatter, showLegend = true, stacked = false,
}) {
    if (!data || data.length === 0) return <EmptyState message="No data for this range" />;
    const Chart = type === 'area' ? AreaChart : LineChart;
    const El = type === 'area' ? Area : Line;
    return (
        <Box sx={{ width: '100%', height }}>
            <ResponsiveContainer>
                <Chart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.muted} />
                    <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={yFormatter} />
                    <RTooltip formatter={(v) => yFormatter ? yFormatter(v) : v} />
                    {showLegend && <Legend wrapperStyle={{ fontSize: 11 }} />}
                    {series.map((s, i) => (
                        <El
                            key={s.key}
                            type="monotone"
                            dataKey={s.key}
                            name={s.label || s.key}
                            stroke={s.color || CHART_COLORS.palette[i % CHART_COLORS.palette.length]}
                            fill={s.color || CHART_COLORS.palette[i % CHART_COLORS.palette.length]}
                            fillOpacity={type === 'area' ? 0.2 : 1}
                            strokeWidth={2}
                            dot={false}
                            stackId={stacked ? 'a' : undefined}
                        />
                    ))}
                </Chart>
            </ResponsiveContainer>
        </Box>
    );
}

// ---------- SimpleBarChart ----------
export function SimpleBarChart({
    data, xKey, series, height = 260, layout = 'horizontal',
    yFormatter, showLegend = true, stacked = false,
}) {
    if (!data || data.length === 0) return <EmptyState message="No data for this range" />;
    return (
        <Box sx={{ width: '100%', height }}>
            <ResponsiveContainer>
                <BarChart data={data} layout={layout} margin={{ top: 8, right: 12, left: layout === 'vertical' ? 60 : 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.muted} />
                    {layout === 'horizontal' ? (
                        <>
                            <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} tickFormatter={yFormatter} />
                        </>
                    ) : (
                        <>
                            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={yFormatter} />
                            <YAxis type="category" dataKey={xKey} tick={{ fontSize: 11 }} width={100} />
                        </>
                    )}
                    <RTooltip formatter={(v) => yFormatter ? yFormatter(v) : v} />
                    {showLegend && series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
                    {series.map((s, i) => (
                        <Bar
                            key={s.key}
                            dataKey={s.key}
                            name={s.label || s.key}
                            fill={s.color || CHART_COLORS.palette[i % CHART_COLORS.palette.length]}
                            stackId={stacked ? 'a' : undefined}
                            radius={[3, 3, 0, 0]}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
}

// ---------- DonutChart ----------
export function DonutChart({ data, nameKey = 'name', valueKey = 'value', height = 220, showLabels = false, formatter }) {
    if (!data || data.length === 0) return <EmptyState message="No data" />;
    return (
        <Box sx={{ width: '100%', height }}>
            <ResponsiveContainer>
                <PieChart>
                    <Pie
                        data={data}
                        dataKey={valueKey}
                        nameKey={nameKey}
                        innerRadius="55%"
                        outerRadius="80%"
                        paddingAngle={2}
                        label={showLabels}
                    >
                        {data.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS.palette[i % CHART_COLORS.palette.length]} />
                        ))}
                    </Pie>
                    <RTooltip formatter={(v) => formatter ? formatter(v) : v} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
            </ResponsiveContainer>
        </Box>
    );
}

// ---------- RadialGauge ----------
export function RadialGauge({ value, max = 100, label, size = 140, color }) {
    const pct = Math.max(0, Math.min(100, (value / max) * 100));
    const barColor = color || (pct >= 80 ? CHART_COLORS.success : pct >= 50 ? CHART_COLORS.warning : CHART_COLORS.danger);
    return (
        <Box sx={{ width: size, height: size, position: 'relative' }}>
            <ResponsiveContainer>
                <RadialBarChart innerRadius="70%" outerRadius="100%" data={[{ value: pct, fill: barColor }]} startAngle={90} endAngle={-270}>
                    <RadialBar background={{ fill: CHART_COLORS.muted }} dataKey="value" cornerRadius={6} />
                </RadialBarChart>
            </ResponsiveContainer>
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h5" fontWeight={700}>{typeof value === 'number' ? value.toFixed(0) : value}</Typography>
                {label && <Typography variant="caption" color="text.secondary">{label}</Typography>}
            </Box>
        </Box>
    );
}

// ---------- SectionHeader ----------
export function SectionHeader({ title, subtitle, actions, icon }) {
    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
            <Stack spacing={0.25}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                    {icon}
                    <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
                </Stack>
                {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
            </Stack>
            {actions && <Box>{actions}</Box>}
        </Stack>
    );
}

// ---------- StatusPill ----------
export function StatusPill({ status, label, size = 'small' }) {
    const lower = (status || '').toString().toLowerCase();
    const map = {
        // success
        active: 'success', done: 'success', completed: 'success', clean: 'success', verified: 'success',
        paid: 'success', posted: 'success', matched: 'success', approved: 'success', cleared: 'success',
        // warning
        pending: 'warning', warning: 'warning', review: 'warning', draft: 'warning', partial: 'warning',
        // danger
        failed: 'error', error: 'error', critical: 'error', rejected: 'error', overdue: 'error', unmatched: 'error',
        // info
        processing: 'info', scheduled: 'info', queued: 'info', new: 'info',
    };
    const color = map[lower] || 'default';
    return <Chip size={size} color={color} label={label || status} />;
}

// ---------- EmptyState ----------
export function EmptyState({ message = 'No data yet', description, action, icon }) {
    return (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <Box sx={{ mb: 1, opacity: 0.5 }}>
                {icon || <InboxIcon sx={{ fontSize: 40 }} />}
            </Box>
            <Typography variant="body2" fontWeight={500}>{message}</Typography>
            {description && <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>{description}</Typography>}
            {action && <Box sx={{ mt: 1.5 }}>{action}</Box>}
        </Box>
    );
}

// ---------- LoadingBlock ----------
export function LoadingBlock({ rows = 3, height = 40 }) {
    return (
        <Stack spacing={0.5}>
            {Array.from({ length: rows }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={height} />
            ))}
        </Stack>
    );
}

// ---------- ConfidenceBar ----------
export function ConfidenceBar({ value, threshold = 0.7, label }) {
    if (value == null) return null;
    const pct = Math.max(0, Math.min(100, value * 100));
    const color = value >= 0.9 ? 'success' : value >= threshold ? 'warning' : 'error';
    return (
        <Box sx={{ minWidth: 100 }}>
            {label && <Typography variant="caption" color="text.secondary">{label}</Typography>}
            <Stack direction="row" spacing={0.5} alignItems="center">
                <LinearProgress variant="determinate" value={pct} color={color} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
                <Typography variant="caption" sx={{ minWidth: 32, textAlign: 'right', fontWeight: 600 }}>
                    {pct.toFixed(0)}%
                </Typography>
            </Stack>
        </Box>
    );
}

// ---------- AIAnalysisBox ----------
export function AIAnalysisBox({ text, title = 'AI Analysis' }) {
    if (!text) return null;
    return (
        <Paper variant="outlined" sx={{
            p: 1.5, mt: 1,
            bgcolor: 'rgba(14,165,233,0.05)',
            borderLeft: '3px solid',
            borderColor: 'info.main',
        }}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                <SmartToyIcon sx={{ fontSize: 16, color: 'info.main' }} />
                <Typography variant="caption" fontWeight={600} color="info.main">{title}</Typography>
            </Stack>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary' }}>{text}</Typography>
        </Paper>
    );
}

// ---------- DrillTable ----------
// A clickable table where each row can expand or navigate.
export function DrillTable({ columns, rows, onRowClick, emptyMessage = 'No rows', rowKeyField = 'id', maxHeight, dense = true }) {
    if (!rows || rows.length === 0) return <EmptyState message={emptyMessage} />;
    return (
        <Box sx={{ maxHeight, overflow: maxHeight ? 'auto' : 'visible' }}>
            <Table size={dense ? 'small' : 'medium'} stickyHeader={!!maxHeight}>
                <TableHead>
                    <TableRow>
                        {columns.map(c => (
                            <TableCell key={c.key} align={c.align || 'left'} sx={{ fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>
                                {c.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r, idx) => (
                        <TableRow
                            key={r[rowKeyField] || idx}
                            hover={!!onRowClick}
                            onClick={onRowClick ? () => onRowClick(r) : undefined}
                            sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                        >
                            {columns.map(c => (
                                <TableCell key={c.key} align={c.align || 'left'}>
                                    {c.render ? c.render(r) : r[c.key]}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
}

// ---------- ActionBar ----------
export function ActionBar({ actions = [], align = 'right' }) {
    return (
        <Stack direction="row" spacing={1} justifyContent={align}>
            {actions.map((a, i) => (
                <Button
                    key={i}
                    size="small"
                    variant={a.variant || 'outlined'}
                    color={a.color || 'primary'}
                    startIcon={a.icon}
                    disabled={a.disabled || a.loading}
                    onClick={a.onClick}
                >
                    {a.loading ? <CircularProgress size={14} /> : a.label}
                </Button>
            ))}
        </Stack>
    );
}

// ---------- NumberStat ----------
export function NumberStat({ value, label, color = 'text.primary', size = 'large' }) {
    const variant = size === 'large' ? 'h4' : size === 'medium' ? 'h5' : 'h6';
    return (
        <Stack spacing={0} alignItems="center">
            <Typography variant={variant} sx={{ fontWeight: 700, color, lineHeight: 1.1 }}>
                {value}
            </Typography>
            {label && <Typography variant="caption" color="text.secondary">{label}</Typography>}
        </Stack>
    );
}

// ---------- ConfidenceChip (reused from CategorizationAgent) ----------
export function ConfidenceChip({ value, source, size = 'small' }) {
    if (value == null) return null;
    const pct = (value * 100).toFixed(0);
    const color = value >= 0.9 ? 'success' : value >= 0.7 ? 'warning' : 'error';
    return (
        <Chip
            size={size}
            color={color}
            icon={source === 'blended_ai' || source === 'ai' ? <SmartToyIcon sx={{ fontSize: 14 }} /> : undefined}
            label={`${pct}%`}
        />
    );
}

// ---------- SeverityBadge ----------
export function SeverityBadge({ level, size = 'small' }) {
    const l = (level || '').toString().toLowerCase();
    const map = { low: 'info', medium: 'warning', high: 'error', critical: 'error', info: 'default' };
    const color = map[l] || 'default';
    return <Chip size={size} color={color} label={l.toUpperCase() || 'N/A'} />;
}
